import React, { useState, useEffect } from 'react';
import {
    Row, Col, Form, Label, Input, Card, CardBody,
    Container, Button, FormFeedback, InputGroup
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import "flatpickr/dist/themes/material_blue.css";
import Flatpickr from "react-flatpickr";

const DiscountsCreate = () => {
    const [title, setTitle] = useState('');
    const [discountType, setCode] = useState('');
    const [value, setValue] = useState('');
    const [scope, setScope] = useState('');
    const [type, setType] = useState('');
    const [category, setCategory] = useState('');
    const [trips, setTrips] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicles, setSelectedVehicles] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);

    useEffect(() => {
        if (scope === "individual") {
            fetchCustomers();
        }
        if (discountType === "vehicles") {
            fetchVehicles();
        }
    }, [scope, discountType]);

    const navigate = useNavigate();

    const validateFields = () => {
        const e = {};
        if (!title.trim()) e.title = "Title is required";
        if (!value) e.value = "Value is required";

        if (type === "percent" && Number(value) > 100) {
            e.value = "Percent value cannot be greater than 100";
        }

        if (!discountType) e.discountType = "Discount Type is required";
        if (discountType === "vehicles" && !selectedVehicles) e.vehicles = "Please select a vehicle";

        if (!scope) e.scope = "Scope is required";
        if (scope === "individual" && !selectedCustomer) e.customer = "Please select a customer";
        if (!type) e.type = "Type is required";
        if (!category) e.category = "Category is required";

        if (category === "dates") {
            if (!startDate) e.startDate = "Start date is required";
            if (!endDate) e.endDate = "End date is required";
        }
        if (category === "trip" && !trips) {
            e.trips = "Number of trips is required";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);

        if (!validateFields()) return;

        setLoading(true);

        const formData = new FormData();
        formData.append('discount_type', discountType);
        formData.append('discount_scope', scope);
        formData.append('discount_category', category);
        formData.append('discount_name', title);
        formData.append('discount_amount_value', value);
        formData.append('discount_amount_type', type);

        if (scope === "individual" && selectedCustomer) {
            formData.append("user_type_id", selectedCustomer.value);
        }

        if (discountType === "vehicles" && setSelectedVehicles) {
            formData.append("vehicle_type_id", setSelectedVehicles.value);
        }

        if (category === "dates") {
            formData.append('start_date', startDate ? startDate.toISOString().split("T")[0] : "");
            formData.append('end_date', endDate ? endDate.toISOString().split("T")[0] : "");
        }

        if (category === "trip") {
            formData.append('trip_count', trips);
        }

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/discounts/create`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();

            if (response.status === 200) {
                toast.success('Discount created successfully', { autoClose: 3000 });
                setLoading(false);
                navigate(`/discounts`);
            } else {
                setLoading(false);
                toast.error(data?.message || 'Error creating Discount', { autoClose: 3000 });
            }
        } catch (err) {
            setLoading(false);
            console.log(err);
            toast.error("Failed to create Discount", { autoClose: 3000 });
        }
    };

    const fetchCustomers = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/customers`, {
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                }
            });

            const data = await response.json();
            if (response.status === 200) {
                const formatted = data.data.map(c => ({
                    value: c.id,
                    label: `${c.user_name} (${c.email})`
                }));
                setCustomers(formatted);
            } else {
                toast.error("Failed to fetch customers");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error fetching customers");
        }
    };

    const fetchVehicles = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/vehicles-categories`, {
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                }
            });

            const data = await response.json();
            if (response.status === 200) {
                const formatted = data.data.map(c => ({
                    value: c.id,
                    label: `${c.displayname}`
                }));
                setVehicles(formatted);
            } else {
                toast.error("Failed to fetch vehicles");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error fetching vehicles");
        }
    };

    document.title = "Add Discount | Moeen";

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs maintitle="Home" title="Discounts" breadcrumbItem="Add Discount" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            {/* Title */}
                                            <div className="mb-3">
                                                <Label>Name</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter promo title"
                                                    value={title}
                                                    onChange={e => setTitle(e.target.value)}
                                                    invalid={isSubmitted && !!errors.title}
                                                />
                                                {isSubmitted && errors.title && (
                                                    <FormFeedback>{errors.title}</FormFeedback>
                                                )}
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Discount Type */}
                                            <div className="mb-3">
                                                <Label>Discount Type</Label>
                                                <Select
                                                    value={discountType ? { value: discountType, label: discountType.charAt(0).toUpperCase() + discountType.slice(1) } : null}
                                                    onChange={(selected) => setCode(selected?.value || "")}
                                                    options={[
                                                        { value: "rides", label: "For Rides" },
                                                        { value: "vehicles", label: "For Vehicles" }
                                                    ]}
                                                    placeholder="-- Select Discount Type --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.discountType && (
                                                    <div className="text-danger mt-1">{errors.discountType}</div>
                                                )}
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Value */}
                                            <div className="mb-3">
                                                <Label>Value</Label>
                                                <Input
                                                    type="number"
                                                    placeholder="Enter value"
                                                    value={value}
                                                    onChange={e => setValue(e.target.value)}
                                                    invalid={isSubmitted && !!errors.value}
                                                />
                                                {isSubmitted && errors.value && (
                                                    <FormFeedback>{errors.value}</FormFeedback>
                                                )}
                                            </div>
                                        </Col>

                                        {/* Show Customer Select only if Individual */}
                                        {scope === "individual" && (
                                            <Col md="6">
                                                <div className="mb-3">
                                                    <Label>Select Customer</Label>
                                                    <Select
                                                        options={customers}
                                                        value={selectedCustomer}
                                                        onChange={setSelectedCustomer}
                                                        placeholder="Choose a customer..."
                                                        isSearchable
                                                    />
                                                    {isSubmitted && errors.customer && (
                                                        <div className="text-danger mt-1">{errors.customer}</div>
                                                    )}
                                                </div>
                                            </Col>
                                        )}

                                        {/* Show Vehicles Select only if Vehicle */}
                                        {discountType === "vehicles" && (
                                            <Col md="6">

                                                <div className="mb-3">
                                                    <Label>Select Vehicle</Label>
                                                    <Select
                                                        options={vehicles}
                                                        value={selectedVehicles}
                                                        onChange={setSelectedVehicles}
                                                        placeholder="Choose a vehicle..."
                                                        isSearchable
                                                    />
                                                    {isSubmitted && errors.vehicles && (
                                                        <div className="text-danger mt-1">{errors.vehicles}</div>
                                                    )}
                                                </div>
                                            </Col>
                                        )}

                                        <Col md="6">
                                            {/* Scope */}
                                            <div className="mb-3">
                                                <Label>Discount Scope</Label>
                                                <Select
                                                    value={scope ? { value: scope, label: scope.charAt(0).toUpperCase() + scope.slice(1) } : null}
                                                    onChange={(selected) => setScope(selected?.value || "")}
                                                    options={[
                                                        { value: "individual", label: "Individual" },
                                                        { value: "all", label: "All" }
                                                    ]}
                                                    placeholder="-- Select Scope --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.scope && (
                                                    <div className="text-danger mt-1">{errors.scope}</div>
                                                )}
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Type */}
                                            <div className="mb-3">
                                                <Label>Type</Label>
                                                <Select
                                                    value={type ? { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) } : null}
                                                    onChange={(selected) => setType(selected?.value || "")}
                                                    options={[
                                                        { value: "percent", label: "Percent" },
                                                        { value: "amount", label: "Amount" }
                                                    ]}
                                                    placeholder="-- Select Promo Code Type --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.type && (
                                                    <div className="text-danger mt-1">{errors.type}</div>
                                                )}
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Category */}
                                            <div className="mb-3">
                                                <Label>Discount Category</Label>
                                                <Select
                                                    value={category ? { value: category, label: category.charAt(0).toUpperCase() + category.slice(1) } : null}
                                                    onChange={(selected) => setCategory(selected?.value || "")}
                                                    options={[
                                                        { value: "trip", label: "PromoCodes for Trips" },
                                                        { value: "dates", label: "PromoCodes for Dates" }
                                                    ]}
                                                    placeholder="-- Select Promo Code Category --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.category && (
                                                    <div className="text-danger mt-1">{errors.category}</div>
                                                )}
                                            </div>
                                        </Col>

                                        {category === "trip" && (
                                            <Col md="6">
                                                <div className="mb-3">
                                                    <Label>Number of Trips</Label>
                                                    <Input
                                                        type="number"
                                                        placeholder="Enter number of trips"
                                                        value={trips}
                                                        onChange={e => setTrips(e.target.value)}
                                                        invalid={isSubmitted && !!errors.trips}
                                                    />
                                                    {isSubmitted && errors.trips && (
                                                        <FormFeedback>{errors.trips}</FormFeedback>
                                                    )}
                                                </div>
                                            </Col>
                                        )}

                                        {/* Extra inputs based on category */}
                                        {category === "dates" && (
                                            <Col md="6">
                                                <Row>
                                                    <Col md="6">
                                                        <div className="mb-3">
                                                            <Label>Start Date</Label>
                                                            <InputGroup>
                                                                <Flatpickr
                                                                    className="form-control d-block"
                                                                    placeholder="dd M, yyyy"
                                                                    value={startDate}
                                                                    onChange={(date) => setStartDate(date[0])}
                                                                    options={{
                                                                        altInput: true,
                                                                        altFormat: "F j, Y",
                                                                        dateFormat: "Y-m-d",
                                                                        defaultDate: new Date(),
                                                                        minDate: "today"
                                                                    }}
                                                                />
                                                            </InputGroup>
                                                            {isSubmitted && errors.startDate && (
                                                                <FormFeedback>{errors.startDate}</FormFeedback>
                                                            )}
                                                        </div>
                                                    </Col>
                                                    <Col md="6">
                                                        <div className="mb-3">
                                                            <Label>End Date</Label>
                                                            <InputGroup>
                                                                <Flatpickr
                                                                    className="form-control d-block"
                                                                    placeholder="dd M, yyyy"
                                                                    value={endDate}
                                                                    onChange={(date) => setEndDate(date[0])}
                                                                    options={{
                                                                        altInput: true,
                                                                        altFormat: "F j, Y",
                                                                        dateFormat: "Y-m-d",
                                                                        defaultDate: new Date(),
                                                                        minDate: "today"
                                                                    }}
                                                                />
                                                            </InputGroup>
                                                            {isSubmitted && errors.endDate && (
                                                                <FormFeedback>{errors.endDate}</FormFeedback>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        )}

                                    </Row>

                                    <Row>
                                        <Col className="text-end">
                                            <Button type="submit" color="primary" disabled={loading}>
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    "Save"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default DiscountsCreate;
